/**
 * # Banner
 *
 * A banner component used to display important messages or notifications.
 *
 * ## Props
 *
 * - `align?: 'left' | 'center'` - The alignment of the banner content. Default is `left`.
 * - `alignContent?: 'start' | 'center'` - The vertical alignment of the banner content. Default is `start`.
 * - `children: React.ReactNode` - The content of the banner.
 * - `className?: string` - Additional CSS class name for the banner wrapper.
 * - `cardClassName?: string` - Additional CSS class name for the inner card element.
 * - `dismissable?: boolean` - Determines if the banner can be dismissed. Default is `false`.
 * - `onDismissEnd?: Function` - Callback function triggered after the dismiss animation completes.
 * - `type?: 'negative' | 'neutral' | 'positive' | 'info' | 'warning' | 'special'` - The type of the banner. Default is `neutral`.
 * - `border?: boolean` - Determines if the banner has a border. Default is `true`.
 * - `duration?: number` - The duration in milliseconds before the banner automatically dismisses.
 * - `storageKey?: string` - A localStorage key to persist the dismissed state. When provided, the banner will not render if it was previously dismissed.
 * - `data-testid?: string` - Test ID for the banner wrapper element.
 *
 * ## Compound Components
 *
 * - `Banner.Icon` - Renders an icon inside the banner.
 * - `Banner.Btn` - Renders a button inside the banner.
 *
 * ## Usage
 *
 * ```tsx
 * import Banner from './Banner.react';
 *
 * const MyComponent = () => {
 *   return (
 *     <Banner
 *       align="center"
 *       dismissable
 *       onDismissEnd={() => console.log('Banner dismissed')}
 *       type="negative"
 *       border={false}
 *       duration={5000}
 *       storageKey="my-feature-banner"
 *     >
 *       <Banner.Icon icon="exclamation-triangle" />
 *       This is an important message!
 *     </Banner>
 *   );
 * };
 * ```
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import classnames from 'classnames';
import makeConstants from 'lib/makeConstants';

import IconButton from '../IconButton/IconButton.react';
import Icon, {IconProp as IconProps, IconStyle} from '../../atoms/Icon/Icon.react';
import './banner.scss';

export const alignOptions = makeConstants('left', 'center');

export type BannerType = 'negative' | 'neutral' | 'positive' | 'info' | 'warning' | 'special';

export const types = makeConstants('negative', 'neutral', 'positive', 'info', 'warning', 'special');

interface BannerIconProps extends PropsWithClassNameOptional {
  icon: IconProps;
  iconStyle?: IconStyle;
  style?: React.CSSProperties;
}

const BannerIcon = ({className, icon, iconStyle, style, ...rest}: BannerIconProps) => (
  <div className={classnames('m-banner__icon', className)}>
    <Icon {...rest} icon={icon} iconStyle={iconStyle} color='inherit' style={style} />
  </div>
);

interface BannerButtonProps extends PropsWithClassNameOptional {
  onClick: (e: any) => void;
  disabled?: boolean;
  icon: IconProps;
  label?: string;
}

const BannerButton = ({icon, onClick, className, disabled, label}: BannerButtonProps) => {
  const onKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.persist();
      onClick(e);
    }
  };

  return (
    <IconButton
      className={classnames('m-banner__btn self-center', className)}
      icon={icon}
      disabled={disabled}
      label={label}
      aria-hidden={!label}
      onClick={onClick}
      onKeyPress={onKeyPress}
      role='button'
      size='s'
      tabIndex={0}
    />
  );
};

interface BannerProps extends PropsWithChildrenRequired, PropsWithClassNameOptional {
  align?: any;
  alignContent?: string;
  cardClassName?: string;
  dismissable?: boolean;
  onDismissEnd?: Function;
  type?: BannerType;
  border?: boolean;
  duration?: number;
  storageKey?: string;
  'data-testid'?: string;
}

const Banner = ({
  align = alignOptions.left,
  alignContent = 'start',
  children,
  className,
  type = 'neutral',
  border = true,
  'data-testid': dataTestId = '',
  cardClassName,
  dismissable,
  onDismissEnd,
  duration,
  storageKey
}: BannerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    if (!storageKey) return false;
    try {
      return localStorage.getItem(storageKey) === 'dismissed';
    } catch {
      return false;
    }
  });

  const isDismissable = Boolean(dismissable || onDismissEnd);

  const handleDismiss = useCallback(() => {
    if (!isDismissable || isDismissing) return;
    setIsDismissing(true);
  }, [isDismissable, isDismissing]);

  const handleDismissEnd = useCallback(
    (e: Event) => {
      if (!isDismissing || e.target !== e.currentTarget) return;
      setIsDismissed(true);
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, 'dismissed');
        } catch {
          // localStorage may be unavailable
        }
      }
      onDismissEnd?.();
    },
    [isDismissing, onDismissEnd]
  );

  useEffect(() => {
    if (!duration) return;
    const timeoutId = setTimeout(handleDismiss, duration);
    return () => clearTimeout(timeoutId);
  }, [duration, handleDismiss]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.addEventListener('transitionend', handleDismissEnd);
    el.addEventListener('animationend', handleDismissEnd);
    return () => {
      el.removeEventListener('transitionend', handleDismissEnd);
      el.removeEventListener('animationend', handleDismissEnd);
    };
  }, [handleDismissEnd]);

  if (isDismissed) return null;

  return (
    <div
      ref={wrapperRef}
      className={classnames('m-banner', className, {
        'm-banner--dismissing': isDismissing
      })}
      data-testid={dataTestId}
    >
      <div
        className={classnames('m-banner__card', cardClassName, {
          [`m-banner__card--${type}`]: type,
          'border-transparent': !border
        })}
      >
        <div
          className={classnames('m-banner__content', {
            'text-center': align === alignOptions.center,
            'items-start': alignContent === 'start',
            'items-center': alignContent === 'center'
          })}
          data-testid='banner-content'
        >
          {children}
          {isDismissable && (
            <BannerButton
              className='m-banner__btn--dismiss'
              disabled={isDismissing}
              icon={['far', 'times']}
              label='Dismiss banner'
              onClick={handleDismiss}
            />
          )}
        </div>
      </div>
    </div>
  );
};

Banner.Btn = BannerButton;
Banner.Icon = BannerIcon;

export default Banner;
